import React from 'react';
import { DocPageLayout } from '../components/DocPageLayout';
import { InfoBox } from '../components/InfoBox';
import { CodeBlock } from '../components/CodeBlock';
export function FeaturesPage() {
  return <DocPageLayout title="Features" description="Explore the full range of gateway capabilities to enhance your payment integration." data-id="element-1576">
      <h2 className="text-2xl font-bold text-gray-900 mt-6 mb-4" data-id="element-1577">
        Tokenization
      </h2>
      <p className="text-gray-700 mb-4" data-id="element-1578">
        Tokenization replaces sensitive card data with a unique token that can
        be stored safely and used for future transactions. This enables features
        like one-click checkout and recurring payments without storing actual
        card numbers.
      </p>
      <CodeBlock language="JSON" title="Create Token Request" code={`{
  "apiOperation": "TOKENIZE",
  "session": {
    "id": "SESSION0002abcdef1234567890"
  },
  "sourceOfFunds": {
    "type": "CARD"
  }
}

// Response
{
  "token": "9111111111111111",
  "result": "SUCCESS",
  "sourceOfFunds": {
    "provided": {
      "card": {
        "brand": "MASTERCARD",
        "expiry": { "month": "05", "year": "25" },
        "number": "512345xxxxxx0008",
        "scheme": "MASTERCARD"
      }
    },
    "type": "CARD"
  }
}`} data-id="element-1579" />

      <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4" data-id="element-1580">
        Recurring Payments
      </h2>
      <p className="text-gray-700 mb-4" data-id="element-1581">
        Set up automatic recurring charges for subscription-based services. Use
        tokenized card details to process scheduled payments without requiring
        the customer to re-enter their information.
      </p>
      <CodeBlock language="JSON" title="Recurring Payment Request" code={`{
  "apiOperation": "PAY",
  "order": {
    "amount": "29.99",
    "currency": "USD"
  },
  "sourceOfFunds": {
    "type": "SCHEME_TOKEN",
    "token": "9111111111111111"
  },
  "transaction": {
    "frequency": "RECURRING",
    "recurringPaymentAgreement": "RECURRING_AGREEMENT_001"
  }
}`} data-id="element-1582" />

      <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4" data-id="element-1583">
        Refunds and Voids
      </h2>
      <p className="text-gray-700 mb-4" data-id="element-1584">
        Process full or partial refunds for captured transactions, or void
        authorized transactions that have not yet been captured.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6" data-id="element-1585">
        <div className="p-5 border border-gray-200 rounded-lg" data-id="element-1586">
          <h3 className="font-bold text-gray-900 mb-2" data-id="element-1587">Refund</h3>
          <p className="text-sm text-gray-600" data-id="element-1588">
            Returns funds to the customer's account. Can be full or partial.
            Available after capture/settlement.
          </p>
        </div>
        <div className="p-5 border border-gray-200 rounded-lg" data-id="element-1589">
          <h3 className="font-bold text-gray-900 mb-2" data-id="element-1590">Void</h3>
          <p className="text-sm text-gray-600" data-id="element-1591">
            Cancels an authorization before capture. No funds are transferred.
            Must be done before settlement.
          </p>
        </div>
      </div>
      <CodeBlock language="JSON" title="Refund Request" code={`{
  "apiOperation": "REFUND",
  "transaction": {
    "amount": "25.00",
    "currency": "USD"
  }
}`} data-id="element-1592" />

      <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4" data-id="element-1593">
        Multi-Currency Support
      </h2>
      <p className="text-gray-700 mb-4" data-id="element-1594">
        Accept payments in over 150 currencies. The gateway handles currency
        conversion and settlement in your preferred currency. Supported
        currencies include USD, EUR, GBP, AUD, CAD, JPY, and many more.
      </p>

      <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4" data-id="element-1595">
        Webhook Notifications
      </h2>
      <p className="text-gray-700 mb-4" data-id="element-1596">
        Receive real-time notifications when transaction events occur. Configure
        webhook URLs to be notified about successful payments, declines,
        refunds, chargebacks, and other events.
      </p>
      <CodeBlock language="JSON" title="Webhook Payload Example" code={`{
  "eventType": "TRANSACTION_UPDATED",
  "order": {
    "id": "ORDER-12345",
    "status": "CAPTURED"
  },
  "transaction": {
    "id": "TXN-001",
    "type": "PAYMENT",
    "result": "SUCCESS",
    "amount": 100.00,
    "currency": "USD"
  },
  "timestamp": "2025-01-15T10:30:00.000Z"
}`} data-id="element-1597" />

      <InfoBox type="tip" title="Webhook Security" data-id="element-1598">
        Always validate webhook signatures to ensure notifications are genuinely
        from the gateway. Use the shared secret provided in your merchant
        configuration to verify the HMAC signature in the request header.
      </InfoBox>

      <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4" data-id="element-1599">
        Reporting & Analytics
      </h2>
      <p className="text-gray-700 mb-4" data-id="element-1600">
        Access detailed transaction reports and analytics through the Merchant
        Administration portal or via the Reporting API. Generate reports for
        reconciliation, settlement, and business intelligence.
      </p>
      <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4" data-id="element-1601">
        <li data-id="element-1602">Transaction search and filtering</li>
        <li data-id="element-1603">Settlement and reconciliation reports</li>
        <li data-id="element-1604">Chargeback and dispute tracking</li>
        <li data-id="element-1605">Custom date range exports (CSV, JSON)</li>
        <li data-id="element-1606">Real-time dashboard with key metrics</li>
      </ul>
    </DocPageLayout>;
}